import { defineDocument } from './types.js'
import {
  SYNCED_ACTIONS,
  ALL_TABS, BASIC_ACTIONS, FULL_ACTIONS, SENDABLE_ACTIONS,
  adjustmentsGrid, baseColumns, baseFilters, chargesGrid, col, f,
  currencySection, fcyColumns, interstate, jobType, pricedLines, taxUnit, taxUnitChip, termsSection,
  withInvoiceLineInfo,
  OURS, ofParty, ofPartyOrOurs, scoped, opt,
} from './common.js'
import { byParty, byProject, many, narrowed, ofKind, stillOpen } from './lookup-narrowing.js'
import type { ColumnDef, FieldDef, FilterDef, LineColumnDef, LineGridDef, SectionDef } from './types.js'
import { INTEGRATION_STATUS_LABELS } from '../integration/registry.js'

/** Who sent it for approval, on the lists the reference offers it on (BILL-032, BILL-039). */
const submittedByFilter: FilterDef = { key: 'submittedById', label: 'Submitted by', control: 'lookup', lookup: { entity: 'employee' } }

/** Where a document stands with the accounting system, as a list filter (BILL-023). */
const integrationFilter: FilterDef = {
  key: 'integrationStatus', label: 'Integration status', control: 'select',
  options: Object.entries(INTEGRATION_STATUS_LABELS).map(([value, label]) => ({ value, label })),
}

/** …and as columns: the state, and what it went into the books as (BILL-022). */
const integrationColumns = (): ColumnDef[] => [
  opt('integrationStatus', 'Integration status', 'text', 170),
  opt('integrationRef', 'Tally ref', 'text', 150),
]

/**
 * The term a BOQ line bills under, as the reference splits it (BILL-026):
 * the term ("Pro rata", "Standard") and the line of it ("On supply").
 * Read off the project's billing terms on every read, never typed.
 */
const termColumns = (): LineColumnDef[] => [
  { key: 'billingTerm', label: 'Billing term', type: 'text', width: 150 },
  { key: 'billingTermLine', label: 'Billing term line', type: 'text', width: 190 },
]

/**
 * "BOQ Info" on a sales invoice or credit note (BILL-038, BILL-043): BOQ
 * lines taken from the billing requests named, with what the BOQ ordered and
 * at what rate beside the quantity billed now. Picked through "Select items"
 * — never typed, so every line is against a request line.
 */
const boqInvoiceLines = (n: string, pickLabel: string): LineGridDef => ({
  title: 'BOQ info', n,
  columns: [
    // The reference's order (INV-008): Ref no, BOQ, Billing Term, Billing
    // Term Line, Order Qty, BOQ Rate, Quantity, HSN/SAC Code, Tax.
    col.seq(), { ...col.boqRef(), label: 'Ref no' }, col.item('BOQ', false), col.desc(), ...termColumns(),
    { key: 'orderQty', label: 'Order qty', type: 'qty', width: 112, align: 'right' },
    { key: 'boqRate', label: 'BOQ rate', type: 'rate', width: 120, align: 'right' },
    col.rate(), col.qty('Quantity'), col.unit(false), col.amount(), col.hsn(), col.tax(),
  ],
  sources: [{ key: 'billing', label: pickLabel, source: 'billing' }],
})

/**
 * "Invoice Items" (BILL-027): anything billed that is not a BOQ line, in a
 * grid of its own and counted apart from the BOQ lines.
 */
const invoiceItems = (n: string): LineGridDef => ({
  section: 'INVOICE_ITEM', title: 'Invoice items', n, addLabel: 'Add items',
  columns: [
    col.seq(), col.item('Item'), col.desc(), col.qty('Quantity'), col.unit(), col.rate(), col.discount(),
    col.amount(), col.hsn(), col.tax(),
    // PRN-029: given at no charge — printed "Free", the rate zeroed.
    { key: 'isFree', label: 'Free?', type: 'text', width: 96, editable: true,
      options: [{ value: 'NO', label: 'No' }, { value: 'YES', label: 'Yes' }],
      sets: { YES: { rate: '0', discountPct: '0' } } },
  ],
  sources: [],
})

/*
  The pickers the reference cascades (BILL-002, BILL-024, BILL-034).

  A supplier's own approved orders, several of them on one invoice; a
  customer's own projects; a project's own billing requests of the right
  kind with something still left to invoice.
*/
const supplierOrders = (key: string, label: string, entity: string, required = false): FieldDef =>
  many(narrowed(f.look(key, label, entity, required), byParty('supplierId')))
/*
  "Projects *" (INV-007, INV-054): a request, invoice or credit note may cover
  several of the customer's projects. The first is the document's project —
  the header column every list, report and scope check reads — and the rest
  are kept beside it; Select BOQ offers the BOQs of all of them, and the
  billing-request picker the requests of all of them.
*/
const customerProjects = (): FieldDef =>
  many(narrowed(f.look('projectId', 'Projects', 'project', true), byParty('customerId')))
const projectRequests = (kind: 'INVOICE' | 'CREDIT_NOTE', multiple: boolean): FieldDef => {
  const field = narrowed(f.look('billingRequestId', 'Billing request', 'billing_request', true),
    byProject(), ofKind(kind), stillOpen())
  return multiple ? many(field) : field
}

/**
 * "Enter document number manually" and the number, first in General Info as
 * the reference has them (INV-009, INV-011) — where the company allows typed
 * numbers at all (Settings › manual numbering for invoices).
 */
const manualNumberFields = (label: string, hint?: string): FieldDef[] => [
  { ...f.yesNo('manualNumber', 'Enter document number manually'), requiresSetting: 'manual_numbering_invoices' },
  { key: 'documentNo', label, control: 'text', width: 'half', required: true,
    requiresSetting: 'manual_numbering_invoices', visibleWhen: { field: 'manualNumber', equals: ['true'] },
    ...(hint ? { hint } : {}) },
]

/** The reference's default view of a sales invoice or credit note (INV-017). */
const invoicingColumns = (): ColumnDef[] => [
  { key: 'customerName', label: 'Customer', type: 'text' },
  docNoColumn,
  docDateColumn,
  { key: 'projectNames', label: 'Projects', type: 'text' },
  { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
  { key: 'total', label: 'Total', type: 'money', width: 150, align: 'right' },
]

/** "Invoice For?" as the reference words it (INV-016). */
const INVOICE_FOR: ReadonlyArray<[string, string]> = [['PURCHASE_ORDERS', 'Purchase orders'], ['SUBCONTRACT', 'Labour/SCO']]
/** The orders a purchase invoice may name; any one of them fixes its project and job type (INV-004). */
const INVOICE_ORDERS = ['purchaseOrderId', 'subcontractOrderId', 'workProgressId'] as const

/** The reference's card titles for the shared grids and terms (INV-016). */
const termsOf = (): SectionDef => ({ ...termsSection(), title: 'Terms and condition' })
const adjustmentsOf = (): LineGridDef => ({ ...adjustmentsGrid(), title: 'Adjustments (deductions / additions)' })

/**
 * The money columns the reference's Create View offers on the invoicing lists
 * (INV-018), each in the books' currency and as entered. Filled per page by
 * the list catalogue: before tax, after tax, and after the deductions and
 * additions — which is the document total.
 */
const moneyCatalogue = (charges = true): ColumnDef[] => [
  opt('subtotal', 'Subtotal', 'money', 140),
  opt('subtotalFcy', 'Subtotal (FCY)', 'money', 150),
  ...(charges ? [opt('chargesTotal', 'Additional charges', 'money', 150), opt('chargesTotalFcy', 'Additional charges (FCY)', 'money', 170)] : []),
  opt('totalBeforeTax', 'Total before tax', 'money', 150),
  opt('totalBeforeTaxFcy', 'Total before tax (FCY)', 'money', 170),
  // The same cell the stock lists show (STK-019), in the document's currency.
  opt('totalAfterTax', 'Total after tax (FCY)', 'money', 170),
  opt('totalWithAdjustment', 'Total with adjustment', 'money', 160),
  opt('totalWithAdjustmentFcy', 'Total with adjustment (FCY)', 'money', 180),
]

/** The list columns the invoicing lists share, in the reference's words (INV-017). */
const docNoColumn: ColumnDef = { key: 'documentNo', label: 'Document number', type: 'docno', width: 160 }
const docDateColumn: ColumnDef = { key: 'documentDate', label: 'Document date', type: 'date', width: 136 }

/** A billing request's Type (INV-006). */
const REQUEST_TYPES: ReadonlyArray<[string, string]> = [['INVOICE', 'Invoice'], ['CREDIT_NOTE', 'Credit note']]

/** A delivery challan's Type. */
const CHALLAN_TYPES: ReadonlyArray<[string, string]> = [
  ['DELIVERY_TO_SITE', 'Delivery to site'],
  ['RETURN_FROM_SITE', 'Return from site'],
  ['SITE_TO_SITE', 'Site to site'],
]

/** docs/07 §1 — what the supplier billed us, against orders or progress. */
export const purchaseInvoice = defineDocument({
  key: 'purchase_invoice',
  group: 'Invoice',
  blurb: 'Create invoices for purchase and subcontract orders. You can also view existing invoices.',
  route: 'pur_invs',
  label: 'Purchase invoice',
  labelPlural: 'Purchase invoices',
  numbering: 'PURCHASE_INVOICE',
  numberPrefix: 'PI-',
  numberWidth: 8,
  workflow: 'PURCHASE_INVOICE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a purchase invoice',
    subtitle: 'Select the invoice for, the supplier and the purchase orders, labour or subcontract orders it covers.',
    fields: [
      f.radio('invoiceFor', 'Invoice for?', INVOICE_FOR),
      f.look('supplierId', 'Supplier', 'supplier', true),
      { ...supplierOrders('purchaseOrderId', 'Purchase orders', 'purchase_order', true),
        visibleWhen: { field: 'invoiceFor', equals: ['PURCHASE_ORDERS'] } },
      { ...supplierOrders('subcontractOrderId', 'Subcontract order', 'subcontract_order'),
        visibleWhen: { field: 'invoiceFor', equals: ['SUBCONTRACT'] },
        hint: 'If order is not issued to the subcontractor then leave this field blank.' },
      // Against a purchase order the order already fixes the tax unit, and the
      // invoice takes it from there (INV-024); asked only without one.
      { ...taxUnit(true), width: 'full' as const, visibleWhen: { field: 'invoiceFor', equals: ['SUBCONTRACT'] } },
    ],
  },
  form: {
    sections: [
      /*
        The reference's General Info, in its order (INV-003, PROJ1-040): what
        it is for, the supplier, the orders, the supplier's own invoice date
        and number, then ours, the due date — and the supplier's contact,
        address and GSTIN at the foot of the same card.

        The orders asked are the ones "Invoice for" says (INV-002). A blank
        "Invoice for" is a purchase-order invoice, as everywhere else that
        reads it; migration 235 fills it on invoices saved before it was asked.
      */
      { key: 'general', n: '01', title: 'General info', fields: [
        f.choice('invoiceFor', 'Invoice for', INVOICE_FOR),
        f.look('supplierId', 'Supplier', 'supplier', true),
        { ...supplierOrders('purchaseOrderId', 'Purchase orders', 'purchase_order', true),
          visibleWhen: { field: 'invoiceFor', equals: ['PURCHASE_ORDERS', ''] } },
        { ...supplierOrders('subcontractOrderId', 'Subcontract orders', 'subcontract_order'),
          visibleWhen: { field: 'invoiceFor', equals: ['SUBCONTRACT'] } },
        { ...supplierOrders('workProgressId', 'Work progress', 'work_progress'),
          visibleWhen: { field: 'invoiceFor', equals: ['SUBCONTRACT'] } },
        // Naming the receipt narrows "Select items" to what actually arrived
        // on it, at the quantity that arrived — the vendor's rule is "select
        // the material from the received list", not from the ordered one, and
        // the two differ on every short delivery.
        { ...narrowed(f.look('goodsReceiptId', 'Against receipt note', 'goods_receipt'), byParty('supplierId')),
          visibleWhen: { field: 'invoiceFor', equals: ['PURCHASE_ORDERS', ''] },
          hint: 'Left empty the whole order is offered. Named, only what that receipt took in is.' },
        f.date('supplierInvoiceDate', 'Supplier invoice date', true),
        f.text('supplierInvoiceNo', 'Supplier invoice no.', 'half', true),
        f.date('documentDate', 'Document date', true),
        { key: 'creditPeriodDays', label: 'Credit period (in days)', control: 'number', width: 'half',
          hint: 'The due date is worked out from the supplier invoice date and this.' },
        // Required as the reference has it (INV-019); still filled from the
        // credit period when left empty, so a typed credit period answers it.
        { key: 'dueDate', label: 'Due date', control: 'date', width: 'half', required: true,
          hint: 'Left empty it is the supplier invoice date plus the credit period.' },
        f.notes(),
        // The supplier's own contact, address and the GSTIN it billed under,
        // as they stood on the invoice (BILL-007). Taken from the order when there is one.
        scoped(f.look('supplierContactId', 'Supplier contact', 'contact'), ofParty('supplierId')),
        scoped(f.look('supplierAddressId', 'Supplier address', 'address'), ofParty('supplierId')),
        { ...f.text('supplierGstin', 'Supplier GSTIN'), readOnly: true,
          hint: 'From the supplier address, or the supplier, when the invoice is saved.' },
        /*
          Not asked by the reference: the project and job type come from the
          order and show in Summary › Details (INV-004). Kept for the
          subcontract invoice raised without an order, and read-only once an
          order is named, so they cannot drift from it.
        */
        { ...f.look('projectId', 'Project', 'project'), readOnlyWhenSet: INVOICE_ORDERS,
          hint: 'From the order, when one is named.' },
        { ...jobType(), readOnlyWhenSet: INVOICE_ORDERS },
        taxUnitChip(),
      ] },
      { key: 'billing', n: '02', title: 'Billing and delivery info', fields: [
        scoped(f.look('billingAddressId', 'Billing address', 'address'), OURS),
        scoped(f.look('deliveryAddressId', 'Delivery address', 'address'), OURS),
        // At the foot of the address card, as on every invoicing document but the request (INV-010).
        interstate(),
      ] },
      currencySection(),
      termsOf(),
    ],
  },
  /*
    The reference's Item Info (PUR-005): "+ Add items" opens the row in its
    Item Info dialog, and "Select items" picks from the open lines of the
    orders named, line by line at the quantity being billed (INV-012) — the
    pull-everything button it replaces made a part invoice a matter of
    deleting rows.
  */
  lines: (() => {
    const grid = withInvoiceLineInfo(pricedLines('Item info', '03', 'Item'))
    return {
      ...grid, addLabel: 'Add items', addOpensDialog: true, detailTitle: 'Item Info',
      sources: [{ key: 'order', label: 'Select items', source: 'order' as const }, ...grid.sources],
    }
  })(),
  // The charges on the orders — freight, transport — are selected, not retyped (BILL-011).
  grids: [chargesGrid('80', [{ key: 'charges', label: 'Select charges', source: 'charges' }]), adjustmentsOf()],
  // The reference's default view (BILL-021): Supplier, number, date, the
  // supplier's own invoice number and date, and the orders it bills.
  columns: [
    { key: 'supplierName', label: 'Supplier', type: 'text' },
    docNoColumn,
    docDateColumn,
    { key: 'supplierInvoiceNo', label: 'Supplier invoice no.', type: 'text', width: 170 },
    { key: 'supplierInvoiceDate', label: 'Supplier invoice date', type: 'date', width: 150 },
    { key: 'purchaseOrderId', label: 'Purchase orders', type: 'text', width: 190 },
    { key: 'subcontractOrderId', label: 'Subcontract orders', type: 'text', width: 190 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Total', type: 'money', width: 150, align: 'right' },
    // And what the column chooser offers besides (BILL-022, INV-018).
    opt('projectName', 'Project'),
    opt('workProgressId', 'Work progress', 'text', 190),
    opt('dueDate', 'Due date', 'date', 130),
    ...integrationColumns(),
    ...moneyCatalogue(),
    opt('taxTotal', 'Tax', 'money', 130),
    ...fcyColumns('Total'),
  ],
  filters: [
    ...baseFilters('supplier', 'Supplier'),
    { key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } },
    { key: 'taxUnitId', label: 'Tax unit', control: 'lookup', lookup: { entity: 'tax_unit' } },
    // The reference's drawer and view builder (BILL-023).
    { key: 'invoiceFor', label: 'Invoice for', control: 'select',
      options: INVOICE_FOR.map(([value, label]) => ({ value, label })) },
    { key: 'dueDate', label: 'Due date', control: 'dateRange' },
    integrationFilter,
    { key: 'supplierInvoiceNo', label: 'Supplier invoice no.', control: 'text' },
    { key: 'purchaseOrderId', label: 'Purchase order', control: 'lookup', lookup: { entity: 'purchase_order' } },
    { key: 'subcontractOrderId', label: 'Subcontract order', control: 'lookup', lookup: { entity: 'subcontract_order' } },
  ],
  // The reference's Summary (INV-004): the supplier over the date, the
  // integration state and the orders; the project and job type the order
  // carried show here, not as questions on the form.
  summaryCard: { title: 'party', line: ['documentDate', 'integration', 'purchaseOrderId', 'subcontractOrderId'] },
  summaryDetails: [
    'projectId', 'jobTypeId', 'supplierInvoiceNo', 'supplierInvoiceDate', 'dueDate', 'interstate',
    'supplierGstin', 'taxUnitId', 'remarks', 'currency',
  ],
  actions: SYNCED_ACTIONS,
  rules: ['rate_exceeds_estimation', 'progress_not_invoiceable', 'hsn_missing'],
  allocatesFrom: ['purchase_order', 'subcontract_order', 'goods_receipt', 'work_progress'],
} as const)

/**
 * docs/24 §3.8 — what goes back to a supplier, and what they owe for it.
 *
 * "Raise a debit note, or an urgent product replacement." Material rejected
 * at inspection never entered stock and never counted against the order; the
 * debit note records it leaving, and says whether the supplier is to refund
 * it or replace it. A replacement leaves the order open for the new delivery;
 * a debit is money the supplier owes, set off against their invoice.
 *
 * Material found damaged after it was accepted is different: it IS in stock,
 * so returning it takes it out of the lot it was received into.
 */
export const DEBIT_REASONS = [
  ['QC_REJECTION', 'Rejected at inspection'],
  ['RETURN_FROM_STOCK', 'Returned from stock — damaged or defective'],
  ['SHORT_SUPPLY', 'Billed but not delivered'],
  ['RATE_DIFFERENCE', 'Charged above the order rate'],
] as const satisfies ReadonlyArray<[string, string]>

export const debitNote = defineDocument({
  key: 'debit_note',
  group: 'Invoice',
  blurb: 'Return rejected or defective material to a supplier, for a refund or a replacement.',
  route: 'debit_notes',
  label: 'Debit note',
  labelPlural: 'Debit notes',
  numbering: 'DEBIT_NOTE',
  numberPrefix: 'DN-',
  workflow: 'DEBIT_NOTE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Raise a debit note',
    subtitle: 'Choose the receipt the material came in on, and why it is going back.',
    fields: [
      f.look('supplierId', 'Supplier', 'supplier', true, 'full'),
      f.look('goodsReceiptId', 'Receipt note', 'goods_receipt', true, 'full'),
      f.radio('reason', 'Why is it going back?', DEBIT_REASONS),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.look('supplierId', 'Supplier', 'supplier', true), lockAfterDraft: true },
        { ...f.look('goodsReceiptId', 'Receipt note', 'goods_receipt', true), lockAfterDraft: true },
        f.look('purchaseInvoiceId', 'Against their invoice', 'purchase_invoice'),
        f.date('documentDate', 'Date', true),
        { ...f.choice('reason', 'Why is it going back?', DEBIT_REASONS), lockAfterDraft: true },
        f.choice('settlement', 'What the supplier is to do', [
          ['DEBIT', 'Refund — debit their account'],
          ['REPLACEMENT', 'Replace it — urgently'],
        ]),
        { ...f.date('replaceBy', 'Replacement needed by'),
          visibleWhen: { field: 'settlement', equals: ['REPLACEMENT'] } },
        f.text('returnedVia', 'Returned via (vehicle / LR)'),
        f.look('projectId', 'Project', 'project'),
        taxUnitChip(),
        interstate(),
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Materials going back', n: '02',
    columns: [
      col.seq(), col.item('Material', false), col.desc(),
      { key: 'rejectionReason', label: 'Why', type: 'text', width: 180, editable: true,
        options: [
          { value: 'DAMAGED', label: 'Damaged in transit' },
          { value: 'SPEC_MISMATCH', label: 'Not to specification' },
          { value: 'WRONG_ITEM', label: 'Wrong item or make' },
          { value: 'QUALITY_FAIL', label: 'Failed quality test' },
          { value: 'NO_CERTIFICATE', label: 'No test certificate' },
          { value: 'DEFECTIVE', label: 'Found defective in use' },
          { value: 'OTHER', label: 'Other' },
        ] },
      col.qty('Returned'), col.unit(false), col.rate(), col.hsn(), col.tax(), col.amount('Debit value'),
    ],
    sources: [{ key: 'rejection', label: 'Pick from the receipt', source: 'rejection' }],
  },
  columns: [
    ...baseColumns('supplier', 'Supplier', 'Debit value'),
    { key: 'settlement', label: 'Settlement', type: 'text', width: 140 },
  ],
  filters: baseFilters('supplier', 'Supplier'),
  actions: [...SENDABLE_ACTIONS, 'changeTaxUnit'],
  rules: ['debit_exceeds_rejection', 'insufficient_stock'],
} as const)

/** docs/07 §2 — asking the customer to pay for completed work. */
export const billingRequest = defineDocument({
  key: 'billing_request',
  group: 'Invoice',
  blurb: 'Request the customer to pay for completed work, with previous invoice details included.',
  route: 'bill_reqs',
  label: 'Billing request',
  labelPlural: 'Billing requests',
  numbering: 'BILLING_REQUEST',
  numberPrefix: 'BR No.{CO}',
  workflow: 'BILLING_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a billing request',
    subtitle: 'Select the customer and the required project.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      customerProjects(),
      f.radio('requestType', 'Request type?', REQUEST_TYPES),
    ],
  },
  form: {
    sections: [
      /*
        The reference's General Info, in its order (INV-006): Customer and
        Projects, Period To and Document Date, Type and Request No, then
        Interstate and Remarks. A request saved before the type was asked is
        an invoice request (migration 235 says so on the record).
      */
      { key: 'general', n: '01', title: 'General info', fields: [
        f.look('customerId', 'Customer', 'customer', true),
        customerProjects(),
        // What the bill runs to is what the customer reads it against (BILL-025).
        f.date('periodTo', 'Period to', true),
        f.date('documentDate', 'Document date', true),
        { ...f.radio('requestType', 'Type', REQUEST_TYPES), width: 'half' as const },
        f.text('requestNo', 'Request no', 'half'),
        interstate(),
        f.notes(),
        // Not asked by the reference; kept, below its questions, for whoever
        // bills a period from a date or by job type.
        f.date('periodFrom', 'Period from'),
        jobType(),
        taxUnitChip(),
      ] },
    ],
  },
  lines: {
    title: 'BOQ info', n: '02',
    // The rows on screen, as a spreadsheet (BILL-026).
    exportable: true,
    // The reference's order (INV-008): Ref no, Bill of Quantities, Billing
    // Term, Billing Term Line, Rate, Quantity, Amount — with ours between.
    columns: [
      col.seq(), { ...col.boqRef(), label: 'Ref no' }, col.item('Bill of quantities'), col.desc(),
      ...termColumns(),
      // What the BOQ ordered, read off the line this one was picked from.
      // Derived on every read, never stored: the request does not own it.
      { key: 'sourceQty', label: 'BOQ qty', type: 'qty', width: 108, align: 'right' },
      // What the work says may be billed, against what has been billed before.
      // The vendor puts all three on the screen — BOQ, Calculated, Billing —
      // because a billing quantity typed without them in view is a guess
      // (docs/video-notes/06 §7).
      { key: 'calculatedQty', label: 'Calculated', type: 'qty', width: 122, align: 'right' },
      { key: 'billedQty', label: 'Billed before', type: 'qty', width: 130, align: 'right' },
      col.rate(),
      /*
        The claim itself, and the column that is actually billed.

        It used to be a separate `requestQty` that rode in `attrs` and was read
        by nothing, while `quantity` — the column the amount, the tax and the
        ledger allocation all follow — was labelled 'BOQ qty' and locked. The
        quantity the operator typed was discarded on every request.
      */
      col.qty('Quantity', true),
      col.unit(), col.amount(), col.hsn(), col.tax(),
    ],
    sources: [
      { key: 'boq', label: 'Select BOQ', source: 'boq' },
      // By BOQ ref, resolved to the same BOQ lines Select BOQ offers.
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  grids: [invoiceItems('03'), chargesGrid(), adjustmentsOf()],
  // Customer, number, date, Type, projects, status, total (BILL-032, INV-017).
  columns: [
    { key: 'customerName', label: 'Customer', type: 'text' },
    docNoColumn,
    docDateColumn,
    { key: 'requestTypeText', label: 'Type', type: 'text', width: 130 },
    { key: 'projectNames', label: 'Projects', type: 'text' },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Total', type: 'money', width: 150, align: 'right' },
    { key: 'fulfilmentStatus', label: 'Invoice/credit status', type: 'text', width: 190 },
    // The column chooser's (INV-018).
    opt('periodTo', 'Period to', 'date', 130),
    opt('requestNo', 'Request no', 'text', 150),
    ...moneyCatalogue(),
    ...fcyColumns('Total'),
  ],
  filters: [
    ...baseFilters('customer', 'Customer'),
    { key: 'requestType', label: 'Type', control: 'select',
      options: REQUEST_TYPES.map(([value, label]) => ({ value, label })) },
    submittedByFilter,
  ],
  // The annexure is downloadable here, before any invoice exists — that copy
  // is what the customer acknowledges (docs/video-notes/06 §6).
  actions: [...FULL_ACTIONS, 'raBill', 'changeTaxUnit'],
  rules: ['billing_request_exceeds_boq', 'billing_rate_differs_from_boq', 'hsn_missing'],
  // Only the action the request's kind allows (BILL-031): an invoice request
  // is invoiced, a credit-note request credited. A request saved before the
  // kind was asked is an invoice request.
  createsNext: [
    { docType: 'sales_invoice', label: 'Sales invoice', whenStatus: 'APPROVED',
      whenField: { field: 'requestType', equals: ['INVOICE', ''] } },
    { docType: 'credit_note', label: 'Credit note', whenStatus: 'APPROVED',
      whenField: { field: 'requestType', equals: ['CREDIT_NOTE'] } },
  ],
} as const)

/** docs/07 §3 — the statutory tax invoice raised to the customer. */
export const salesInvoice = defineDocument({
  key: 'sales_invoice',
  group: 'Invoice',
  blurb: 'Create invoices for billing requests. You can also view existing invoices.',
  route: 'sale_invs',
  label: 'Sales invoice',
  labelPlural: 'Sales invoices',
  numbering: 'SALES_INVOICE',
  numberPrefix: 'INV-',
  workflow: 'SALES_INVOICE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a sales invoice',
    subtitle: 'Select the customer, the projects and the billing requests it invoices.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      customerProjects(),
      projectRequests('INVOICE', true),
    ],
  },
  form: {
    sections: [
      /*
        The reference's General Info (INV-009): the manual-number switch
        first, then Customer and Projects as the wizard fixed them, the dates,
        the customer's PO and Remarks. Tax unit is the chip, Interstate sits in
        Billing Info, and the job type comes from the project (stored values
        are kept on the record; only the question went).
      */
      { key: 'general', n: '01', title: 'General info', fields: [
        // Asked per document (BILL-036), where the company allows typed numbers.
        ...manualNumberFields('Document number'),
        { ...f.look('customerId', 'Customer', 'customer', true), lockAfterDraft: true },
        { ...customerProjects(), lockAfterDraft: true },
        // The wizard's; shown so the invoice says which requests it bills.
        { ...projectRequests('INVOICE', true), lockAfterDraft: true },
        f.date('documentDate', 'Document date', true),
        { key: 'creditPeriodDays', label: 'Credit period (in days)', control: 'number', width: 'half' },
        // Required as the reference has it (INV-019); filled from the credit
        // period when left empty.
        { key: 'dueDate', label: 'Due date', control: 'date', width: 'half', required: true,
          hint: 'Left empty it is the document date plus the credit period.' },
        f.text('customerPoNo', 'Customer PO number'),
        f.date('customerPoDate', 'Customer PO date'),
        // Asked only where the company registers its invoices at all. Shown
        // unconditionally, they were two blanks on every invoice of a tenant
        // that had the switch off.
        { ...f.text('ackNo', 'e-Invoice ack no'), requiresSetting: 'einvoice_enabled' },
        { ...f.date('ackDate', 'e-Invoice ack date'), requiresSetting: 'einvoice_enabled' },
        /*
          What the IRP answers with, printed in the Tax Invoice's e-invoice
          block (PRN-007). Typed in when the IRN was generated on the portal by
          hand; the integration writes them itself once it is switched on.
        */
        { ...f.choice('eInvoiceStatus', 'e-Invoice status', [
          ['', 'Not generated'], ['GENERATED', 'Generated'], ['MANUALLY_GENERATED', 'Manually generated'], ['CANCELLED', 'Cancelled'],
        ], false), requiresSetting: 'einvoice_enabled' },
        { ...f.text('irn', 'IRN', 'full'), requiresSetting: 'einvoice_enabled' },
        { ...f.text('ewayBillNo', 'E-way bill no'), requiresSetting: 'einvoice_enabled' },
        f.notes(),
        taxUnitChip(),
      ] },
      { key: 'billing', n: '02', title: 'Billing info', fields: [
        scoped(f.look('contactId', 'Billing contact', 'contact'), ofParty('customerId')),
        scoped(f.look('billingAddressId', 'Billing address', 'address', true), ofParty('customerId')),
        // Where the goods or the work left from: one of our own (BILL-037).
        scoped(f.look('dispatchAddressId', 'Dispatch address', 'address'), OURS),
        { ...f.text('customerGstin', 'Customer GSTIN'), readOnly: true,
          hint: 'From the billing address, or the customer, when the invoice is saved.' },
        scoped(f.look('deliveryAddressId', 'Delivery address', 'address'), ofPartyOrOurs('customerId')),
        interstate(),
      ] },
      currencySection(),
      termsOf(),
    ],
  },
  lines: boqInvoiceLines('03', 'Select items'),
  grids: [invoiceItems('04'), chargesGrid(), adjustmentsOf()],
  // The reference's All Sales Invoices (INV-017): Customer first.
  columns: [
    ...invoicingColumns(),
    // The column chooser's (BILL-039, INV-018); due date, interstate and the
    // customer PO come from the form itself.
    opt('manualNumberText', 'Enter document number manually', 'text', 200),
    opt('eInvoiceStatusText', 'e-Invoice status', 'text', 150),
    opt('ewayBillNo', 'E-way bill no', 'text', 150),
    ...moneyCatalogue(),
    ...fcyColumns('Total'),
    ...integrationColumns(),
  ],
  filters: [...baseFilters('customer', 'Customer'), submittedByFilter],
  // Same annexure again, now carrying the invoice number the customer's
  // accounts department will quote back.
  actions: [...SYNCED_ACTIONS, 'raBill'],
  rules: ['nothing_left_to_invoice', 'billing_request_kind', 'hsn_missing'],
  allocatesFrom: ['billing_request'],
  createsNext: [{ docType: 'credit_note', label: 'Credit note', whenStatus: 'APPROVED' }],
} as const)

/** docs/07 §4 — reversing part of a sales invoice. */
export const creditNote = defineDocument({
  key: 'credit_note',
  group: 'Invoice',
  blurb: 'Raise credit notes against billing requests. You can also view existing credit notes.',
  route: 'credit_notes',
  label: 'Credit note',
  labelPlural: 'Credit notes',
  numbering: 'CREDIT_NOTE',
  numberPrefix: 'CN-',
  numberWidth: 3,
  workflow: 'CREDIT_NOTE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a credit note',
    subtitle: 'Select the customer, the projects and the billing request being credited.',
    fields: [
      f.look('customerId', 'Customer', 'customer', true),
      customerProjects(),
      projectRequests('CREDIT_NOTE', false),
    ],
  },
  form: {
    sections: [
      /*
        The reference's General Info and Billing Info (INV-011): the
        manual-number switch first, Customer and Projects, the date and
        Remarks; then Billing Contact, Billing Address and Interstate.
      */
      { key: 'general', n: '01', title: 'General info', fields: [
        // Unique within the project: another project may use the same number.
        ...manualNumberFields('Document number', 'Unique within the project: another project may use the same number.'),
        { ...f.look('customerId', 'Customer', 'customer', true), lockAfterDraft: true },
        { ...customerProjects(), lockAfterDraft: true },
        // The wizard asked for the billing request, so the form shows the same
        // one, read-only. An earlier version asked for an invoice here and the
        // two disagreed about what the note was against.
        { ...projectRequests('CREDIT_NOTE', false), readOnlyWhenSet: ['billingRequestId'] },
        f.date('documentDate', 'Document date', true),
        f.notes(),
        // Not asked by the reference (BILL-042); kept for whoever wants to say.
        narrowed(f.look('salesInvoiceId', 'Against invoice', 'sales_invoice'), byProject()),
        f.text('reason', 'Reason', 'full'),
        taxUnitChip(),
      ] },
      { key: 'billing', n: '02', title: 'Billing info', fields: [
        scoped(f.look('contactId', 'Billing contact', 'contact'), ofParty('customerId')),
        scoped(f.look('billingAddressId', 'Billing address', 'address'), ofParty('customerId')),
        interstate(),
      ] },
    ],
  },
  lines: boqInvoiceLines('03', 'Select BOQ'),
  grids: [invoiceItems('04'), adjustmentsOf()],
  columns: [...invoicingColumns(), ...moneyCatalogue(false), ...fcyColumns('Total')],
  filters: baseFilters('customer', 'Customer'),
  actions: SYNCED_ACTIONS,
  rules: ['billing_request_kind', 'hsn_missing'],
  allocatesFrom: ['billing_request'],
} as const)

/** docs/07 §5 — goods leaving for site, with or without an invoice. */
export const deliveryChallan = defineDocument({
  key: 'delivery_challan',
  group: 'Invoice',
  blurb: 'Create a delivery challan for materials delivered to site. You can also view existing challans.',
  route: 'del_clns',
  label: 'Delivery challan',
  labelPlural: 'Delivery challans',
  numbering: 'DELIVERY_CHALLAN',
  numberPrefix: 'DC-',
  workflow: 'DELIVERY_CHALLAN',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Create a delivery challan',
    subtitle: 'Select the customer and the required project.',
    fields: [
      // A dropdown, as the reference asks it (INV-014).
      { ...f.choice('challanType', 'Type', CHALLAN_TYPES), width: 'full' as const },
      f.look('projectId', 'Project', 'project', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General info', fields: [
        // The type and project are the wizard's, fixed for good; the customer
        // is the project's own (BILL-047).
        { ...f.choice('challanType', 'Type', CHALLAN_TYPES, false), readOnly: true },
        { ...f.look('projectId', 'Project', 'project', true), readOnly: true },
        { ...f.look('customerId', 'Customer', 'customer', true), lockAfterDraft: true,
          hint: "The project's customer." },
        f.date('documentDate', 'Document date', true),
        f.notes(),
        // Not asked by the reference; ours, below its questions (INV-015).
        // Read off the lines on every save (BILL-049): every line billable is
        // a billable challan, none is a non-billable one. A mix is what
        // `challan_mixes_billable` refuses, and Split moves the odd ones out.
        { ...f.choice('itemType', 'Item type', [['YES', 'Billable'], ['NO', 'Non billable']], false),
          hint: 'Worked out from the lines when the challan is saved.' },
        // What the goods are moving against — what "Select items" offers.
        f.look('purchaseOrderId', 'Against purchase order', 'purchase_order'),
        f.look('materialTransferId', 'Against material transfer', 'material_transfer'),
        // One mixed receipt is meant to produce two challans — the billable
        // one the customer signs at the gate, and the internal one. Without
        // the receipt as a source there was nothing to raise them from
        // (docs/video-notes/06 §2).
        f.look('goodsReceiptId', 'Against receipt note', 'goods_receipt'),
        f.look('warehouseId', 'Dispatched from', 'warehouse'),
        f.text('vehicleNo', 'Vehicle no'),
        { ...f.text('ewayBillNo', 'e-Way bill no'), requiresSetting: 'ewaybill_enabled' },
        { ...f.choice('ewayBillStatus', 'e-Way bill status', [
          ['', 'Not raised'], ['GENERATED', 'Generated'], ['CANCELLED', 'Cancelled'],
          ['EXPIRED', 'Expired'],
        ], false), requiresSetting: 'ewaybill_enabled' },
        f.date('requestDate', 'Request date'),
        taxUnitChip(),
      ] },
      /*
        The reference's Delivery Info (INV-015): Dispatch Address, Delivered
        Address, the customer's Billing Address shown read-only, the
        coordinator and the contact number, and Interstate at the foot.
      */
      { key: 'delivery', n: '02', title: 'Delivery info', fields: [
        scoped(f.look('dispatchAddressId', 'Dispatch address', 'address'), OURS),
        // Where the goods go is a site, not the customer's office — so it is
        // offered, not assumed.
        scoped(f.look('deliveryAddressId', 'Delivered address', 'address', true), ofPartyOrOurs('customerId'), 'none'),
        // The customer's own billing address, defaulted from the customer.
        { ...scoped(f.look('billingAddressId', 'Billing address', 'address'), ofParty('customerId')), readOnly: true },
        // A person, not a name typed in (BILL-048): their mobile fills the
        // contact number when that is left empty.
        f.look('deliveryCoordinatorId', 'Delivery coordinator', 'employee'),
        f.text('deliveryContactNo', 'Delivery contact no'),
        interstate(),
        scoped(f.look('contactId', 'Site contact', 'contact'), OURS, 'none'),
        f.text('attentionOf', 'For the attention of', 'full'),
      ] },
    ],
  },
  /*
    The reference's Material Info (BILL-050, INV-013): Item, Order Brand,
    Quantity, Rate, Discount, Amount, HSN Code, Tax — the amount straight
    after the figures it comes from — added through "Select items" over the
    open lines of the order, transfer or receipt named.
  */
  lines: {
    title: 'Material info', n: '03',
    columns: [
      col.seq(), col.item('Item'), col.desc(), col.brand('Order brand'), col.qty('Quantity'), col.unit(),
      col.rate(), col.discount(), col.amount(), col.billable(), col.hsn(), col.tax(),
    ],
    sources: [
      { key: 'order', label: 'Select items', source: 'order' },
      { key: 'stock', label: 'Pick from stock', source: 'stock' },
      { key: 'excel', label: 'Import from Excel', source: 'excel' },
    ],
  },
  // Customer, number, date, Type, project, item type, status (BILL-053, INV-017).
  columns: [
    { key: 'customerName', label: 'Customer', type: 'text' },
    docNoColumn,
    docDateColumn,
    { key: 'challanTypeText', label: 'Type', type: 'text', width: 150 },
    { key: 'projectName', label: 'Project', type: 'text' },
    { key: 'itemType', label: 'Item type', type: 'text', width: 150 },
    { key: 'documentStatus', label: 'Status', type: 'status', width: 134 },
    { key: 'total', label: 'Total', type: 'money', width: 150, align: 'right' },
  ],
  filters: [
    ...baseFilters('customer', 'Customer'),
    { key: 'challanType', label: 'Type', control: 'select',
      options: CHALLAN_TYPES.map(([value, label]) => ({ value, label })) },
    { key: 'itemType', label: 'Item type', control: 'select',
      options: [{ value: 'YES', label: 'Billable' }, { value: 'NO', label: 'Non billable' }] },
    submittedByFilter,
  ],
  // `splitBillable` is what makes the mixed case answerable: it moves the
  // lines that disagree with the header onto a challan of their own rather
  // than asking the operator to rebuild them by hand.
  actions: [...BASIC_ACTIONS, 'splitBillable', 'changeTaxUnit'],
  rules: ['challan_exceeds_delivered', 'challan_mixes_billable', 'hsn_missing', 'insufficient_stock'],
  allocatesFrom: ['material_transfer', 'purchase_order', 'goods_receipt'],
} as const)
